import { Injectable } from '@angular/core';
import { ExportContext, ExportFileFormat, ExportRange, ExportService, ExportTaskProcessor } from 'app/layout/export-overlay';
import { buildQuery, page, seed } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { takeUntil } from 'rxjs';
import get from 'lodash-es/get';
import ceil from 'lodash-es/ceil';
import { OrderCustomerService } from '../order-customer.service';
import { LOCALIZED_NECESSITY, LOCALIZED_PROPERTIES, ORDER_CUSTOMER_EXPORT_ID, ORDER_CUSTOMER_TITLE } from './order-customer.constants';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/status.enum';
import { OrderAttributes } from '@albatrosdeveloper/ave-models-package';
import { CompanyService } from 'app/modules/components/configuration/company/company.service.service';

@Injectable()
export class OrderCustomerExport {
  constructor(
    private exportService: ExportService,
    private orderCustomer: OrderCustomerService,
    private companyService: CompanyService
  ) {}

  getStatusNameSpanish(status: number): string {
    return status === StatusEnum.INACTIVE ||
    status === StatusEnum.ORDER_REJECTED
    ? "Si"
    : "No";
  }
  
  stockDecimalsDisplay = 0;

  private formatDecimals(value: number | undefined | null): string {
    if (value == null) return "0.000";
    return value.toFixed(this.stockDecimalsDisplay);
  }

  getStockDecimalDisplay() {
    console.log('Stock Decimals Display:', this.companyService.getActiveCompany().stockDecimalsDisplay);
    this.stockDecimalsDisplay =
      this.companyService.getActiveCompany().stockDecimalsDisplay;
  }

  private transformItem(item: OrderAttributes, id?: string) {
    var transformedItems = {
      code: get(item, "subRoute.externalKeys.code", "??"),
      subRoute: get(item, "subRoute.name", "??"),
      tripCode: get(item, "trip.externalKeys.code", "-"), //only Loss List
      trip: get(item, "trip.id", "-"), ///only Loss List
      user: get(item, "user", "??"),
      serials: get(item, "serials", "??"),
      externalCode: get(item, "externalKeys.code", ""),
      date: ExportTaskProcessor.types.excel.date(get(item, "date", null)),
      returnTypeCode: get(item, "returnType.code", "??"),
      returnTypeName: get(item, "returnType.name", "??"),
      shipToExternalKey: get(item, "shipToExternalKeyCode", "??"),
      shipToName: get(item, "shipToName", "??"),
      NIT: `${get(item, "shipTo.NIT", "")}`,
      soldToExternalKey: get(item, "soldTo.externalKeys.code", ""), //only Loss List
      soldToName: get(item, "soldTo.name", ""), //only Loss List
      materialCode: get(item, "materialName.externalKeys.code", "??"),
      materialName: get(item, "materialName.name", "??"),
      soldQuantity: this.formatDecimals(get(item, "baseQuantity", null)),
      baseUnitOfMeasureCode: `${get(item, "baseUnitOfMeasure.code", "??")} ${get(item, "baseUnitOfMeasure.name", "??")}`,
      pararellQuantity: this.formatDecimals(
        get(item, "pararellQuantity", null)
      ),
      pararellUnitOfMeasureCode: `${get(item, "pararellUnitOfMeasure.code", "??")} ${get(item, "pararellUnitOfMeasure.name", "??")}`,
      unitPrice: ExportTaskProcessor.types.number(get(item, "unitPrice", "??")),
      subTotal: ExportTaskProcessor.types.number(get(item, "subTotal", "??")),
      total: ExportTaskProcessor.types.number(get(item, "total", "??")),
      status: this.getStatusNameSpanish(item.statusId),
    };

    if (id != "order-return-product") {
      delete transformedItems["returnTypeCode"];
      delete transformedItems["returnTypeName"];
    }

    if (id != "order-loss-product") {
      delete transformedItems["soldToExternalKey"];
      delete transformedItems["soldToName"];
    }

    return transformedItems;
  }

  private transformItemForNecessity(item: OrderAttributes) {
    return {
      materialCode: get(item, "code", "??"),
      materialName: get(item, "name", "??"),
      baseQuantity: this.formatDecimals(get(item, "baseQuantity", null)),
      baseUnitOfMeasureCode: `${get(item, "baseUnitOfMeasure.code", "??")} ${get(item, "baseUnitOfMeasure.name", "??")}`,
      pararellQuantity:  this.formatDecimals(get(item, "pararellQuantity", null)),
      pararellUnitOfMeasureCode: `${get(item, "pararellUnitOfMeasure.code", "??")} ${get(item, "pararellUnitOfMeasure.name", "??")}`,
      stockConfirmedBase: this.formatDecimals(get(item, "stockConfirmedBase", null)),
      stockbaseUnitOfMeasureCode: `${get(item, "baseUnitOfMeasure.code", "??")} ${get(item, "baseUnitOfMeasure.name", "??")}`,
      stockConfirmedPararell: this.formatDecimals(get(item, "stockConfirmedPararell", null)),
      stockpararellUnitOfMeasureCode: `${get(item, "pararellUnitOfMeasure.code", "??")} ${get(item, "pararellUnitOfMeasure.name", "??")}`,
    };
  }

  private transformItemAsExcelForNecessity(item: OrderAttributes) {
    const obj = this.transformItemForNecessity(item) as any;
    return obj;
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    id: string,
    title: string
  ) {
    const totalPages = ceil(totalItems / pageSize) + 1;

    if (id != "order-return-product") {
      delete LOCALIZED_PROPERTIES["returnTypeCode"];
      delete LOCALIZED_PROPERTIES["returnTypeName"];
    }

    if (id != "order-loss-product") {
      delete LOCALIZED_PROPERTIES["tripCode"];
      delete LOCALIZED_PROPERTIES["trip"];
      delete LOCALIZED_PROPERTIES["soldToExternalKey"];
      delete LOCALIZED_PROPERTIES["soldToName"];
    }

    this.exportService
      .task({
        id: id,
        title: title,
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(title, LOCALIZED_PROPERTIES);
        },
        next: (ctx: ExportContext) => {
          this.getStockDecimalDisplay();
          const currentPage =
            range === ExportRange.AllPages ? ctx.iteration : pageIndex;
          this.orderCustomer
            .findByFilter(
              buildQuery(
                seed(this.orderCustomer.currentFilter),
                page(currentPage, pageSize)
              ),
              { contextHeader: "export" }
            )
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              get(response, "data.0.data", []).map((item) => {
                if (format === ExportFileFormat.Excel) {
                  ctx.processor.addToSection(
                    title,
                    this.transformItem(item, id)
                  );
                } else {
                  ctx.processor.addToSection(
                    title,
                    this.transformItem(item, id)
                  );
                }
              });
              ctx.next();
            });
        },
      })
      .start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(ORDER_CUSTOMER_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }

  exportNecessity(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    id: string,
    title: string
  ) {
    const totalPages = ceil(totalItems / pageSize) + 1;
    this.exportService
      .task({
        id: id,
        title: title,
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(title, LOCALIZED_NECESSITY);
        },
        next: (ctx: ExportContext) => {
          this.getStockDecimalDisplay();
          const currentPage =
            range === ExportRange.AllPages ? ctx.iteration : pageIndex;
          this.orderCustomer
            .findByNecessity(
              buildQuery(
                seed(this.orderCustomer.currentFilter),
                page(currentPage, pageSize)
              ),
              { contextHeader: "export" }
            )
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              get(response, "data.0.data", []).map((item) => {
                if (format === ExportFileFormat.Excel) {
                  ctx.processor.addToSection(
                    title,
                    this.transformItemAsExcelForNecessity(item)
                  );
                } else {
                  ctx.processor.addToSection(
                    title,
                    this.transformItemForNecessity(item)
                  );
                }
              });
              ctx.next();
            });
        },
      })
      .start();
  }
}
